import {
  type FilterProvider,
  isNestedFilterProvider,
  NOT_SHOWN,
  type SuppliedFilterProvider,
} from '@github-ui/filter-core'
import {NestedFilterProvider, StaticFilterProvider} from '@github-ui/filter-core/providers'
import {IssueTracksIcon} from '@primer/octicons-react'

const SUB_ISSUE_PREFIX = 'sub-issues'
const SUB_ISSUE_DOT_PREFIX = `${SUB_ISSUE_PREFIX}.`

/**
 * Modifies a FilterProvider instance in-place, adding a `sub-issues.` prefix
 * to its key, updating the display name and disabling `has:`/`no:` suggestions.
 */
function prefixProvider(provider: FilterProvider): FilterProvider {
  provider.key = `${SUB_ISSUE_DOT_PREFIX}${provider.key}`
  provider.aliases = provider.aliases?.map(a => `${SUB_ISSUE_DOT_PREFIX}${a}`)
  provider.displayName = `Sub-issues – ${provider.displayName}`
  provider.priority = NOT_SHOWN
  provider.options = {
    ...provider.options,
    priority: NOT_SHOWN,
    filterTypes: {
      ...provider.options.filterTypes,
      valueless: false,
      hasValue: false,
    },
  }
  return provider
}

// Keys that don't make sense as sub-issue filters
const EXCLUDED_KEYS = new Set(['parent-issue', 'sub-issues-progress'])

/**
 * Builds a NestedFilterProvider that wraps fresh provider instances with the
 * `sub-issues.` prefix. The providers passed in should be freshly created instances
 * (not shared with the main filter providers), since this function modifies them
 * in-place.
 */
export function buildSubIssueNestedProvider(providers: Array<SuppliedFilterProvider>): NestedFilterProvider {
  const prefixedProviders: Array<FilterProvider> = []

  for (const provider of providers) {
    if (isNestedFilterProvider(provider)) continue
    if (EXCLUDED_KEYS.has(provider.key)) continue
    prefixedProviders.push(prefixProvider(provider))
  }

  const subIssueFilterKey = {
    key: SUB_ISSUE_PREFIX,
    displayName: 'Sub-issues',
    description: 'Filter by sub-issue properties',
    priority: 5,
    icon: IssueTracksIcon,
  }

  // Add the base "sub-issues." provider that shows sub-key suggestions
  const baseProvider = new StaticFilterProvider({...subIssueFilterKey, key: SUB_ISSUE_DOT_PREFIX}, [], {
    filterTypes: {valueless: false, hasValue: false},
  })

  const nestedProvider = new NestedFilterProvider({...subIssueFilterKey, subKeys: []})
  nestedProvider.filterProviders = [...prefixedProviders, baseProvider]
  return nestedProvider
}
